import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  BeakerIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Select,
  Badge,
  Modal,
  ModalFooter,
  Card,
  Input,
  LoadingSpinner,
} from '@/components/common';
import {
  useEligibilityRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  useBulkImportRules,
  useCheckEligibility,
  useEligibilityStates,
} from '@/hooks/useEligibility';
import { useInsurersLookup } from '@/hooks/useInsurers';
import type { EligibilityRule, CheckEligibilityParams } from '@/services/eligibility';

const ruleTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'REJECT', label: 'REJECT' },
  { value: 'ALLOW', label: 'ALLOW' },
];

// Values are the model's choices (InsurerEligibilityRule.VEHICLE_TYPE_CHOICES etc.).
const VEHICLE_TYPES = [
  { value: 'TWO_WHEELER', label: 'Two Wheeler' },
  { value: 'PRIVATE_CAR', label: 'Private Car' },
  { value: 'COMMERCIAL', label: 'Commercial' },
];

const PRODUCT_VARIANTS = [
  { value: 'comprehensive', label: 'Comprehensive' },
  { value: 'tp_only', label: 'TP Only' },
  { value: 'saod', label: 'Stand Alone OD' },
];

const MANAGED_BY = [
  { value: 'insurer_admin', label: 'Insurer Admin' },
  { value: 'country_admin', label: 'Country Admin' },
  { value: 'broker_admin', label: 'Broker Admin' },
];

const vehicleTypeOptions = [{ value: '', label: 'All Vehicle Types' }, ...VEHICLE_TYPES];
const managedByOptions = [{ value: '', label: 'All' }, ...MANAGED_BY];
const labelOf = (options: { value: string; label: string }[], value?: string | null) =>
  options.find((o) => o.value === value)?.label || value || '';

const emptyForm = {
  insurer: '',
  rule_type: 'REJECT' as 'REJECT' | 'ALLOW',
  managed_by: 'country_admin',
  vehicle_type: 'all',
  product_variant: 'all',
  vehicle_make: '',
  vehicle_model: '',
  state: '',
  city: '',
  rto_code: '',
  pincode: '',
  min_cubic_capacity: '',
  max_cubic_capacity: '',
  min_seating_capacity: '',
  max_seating_capacity: '',
  min_vehicle_age_years: '',
  max_vehicle_age_years: '',
  min_idv: '',
  max_idv: '',
  max_discount_percent: '',
  reason: '',
};

type RuleForm = typeof emptyForm;

const NUMERIC_FIELDS = [
  'min_cubic_capacity',
  'max_cubic_capacity',
  'min_seating_capacity',
  'max_seating_capacity',
  'min_vehicle_age_years',
  'max_vehicle_age_years',
  'min_idv',
  'max_idv',
  'max_discount_percent',
] as const;

/** The API wraps validation errors as {error: {message, fields}}; show its message instead of a generic toast. */
function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: { message?: string }; detail?: string } } })?.response?.data;
  return data?.error?.message || data?.detail || fallback;
}

const emptyCheck: CheckEligibilityParams & { insurer_id: string } = {
  insurer_id: '',
  vehicle_type: '',
  product_variant: '',
  vehicle_make: '',
  vehicle_model: '',
  state_id: '',
  city: '',
  rto_code: '',
  pincode: '',
};

export function EligibilityRulesPage() {
  const [page, setPage] = useState(1);
  const [insurerFilter, setInsurerFilter] = useState('');
  const [ruleTypeFilter, setRuleTypeFilter] = useState('');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('');
  const [managedByFilter, setManagedByFilter] = useState('');

  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  const [selectedRule, setSelectedRule] = useState<EligibilityRule | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [bulkJson, setBulkJson] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [checkForm, setCheckForm] = useState(emptyCheck);
  const [checkNumbers, setCheckNumbers] = useState({ cubic_capacity: '', seating_capacity: '', vehicle_age_years: '', idv: '' });

  const { data, isLoading } = useEligibilityRules({
    insurer: insurerFilter || undefined,
    rule_type: ruleTypeFilter || undefined,
    vehicle_type: vehicleTypeFilter || undefined,
    managed_by: managedByFilter || undefined,
    page,
    page_size: 15,
  });

  const { data: insurersLookup } = useInsurersLookup();
  const { data: statesLookup } = useEligibilityStates();
  const stateOptions = [{ value: '', label: 'Any' }, ...(statesLookup || []).map((st) => ({ value: st.id, label: st.name }))];
  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();
  const deleteMutation = useDeleteRule();
  const bulkMutation = useBulkImportRules();
  const checkMutation = useCheckEligibility();

  const rules = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 15);

  const insurerOptions = [
    { value: '', label: 'All Insurers' },
    ...(insurersLookup || []).map((i) => ({ value: i.id, label: i.name })),
  ];

  const handleOpenRuleModal = (rule?: EligibilityRule) => {
    if (rule) {
      setSelectedRule(rule);
      setFormData({
        insurer: rule.insurer,
        rule_type: rule.rule_type,
        managed_by: rule.managed_by || 'country_admin',
        vehicle_type: rule.vehicle_type || 'all',
        product_variant: rule.product_variant || 'all',
        vehicle_make: rule.vehicle_make || '',
        vehicle_model: rule.vehicle_model || '',
        state: rule.state || '',
        city: rule.city || '',
        rto_code: rule.rto_code || '',
        pincode: rule.pincode || '',
        reason: rule.reason || '',
        ...Object.fromEntries(NUMERIC_FIELDS.map((f) => [f, rule[f] != null ? String(rule[f]) : ''])),
      } as RuleForm);
    } else {
      setSelectedRule(null);
      setFormData(emptyForm);
    }
    setIsRuleModalOpen(true);
  };

  const handleCloseRuleModal = () => {
    setIsRuleModalOpen(false);
    setSelectedRule(null);
  };

  const handleSubmit = async () => {
    try {
      // On edit, a cleared field must be sent as empty (null) so it clears; on create it is just omitted.
      const numeric = Object.fromEntries(
        NUMERIC_FIELDS.map((f) => [f, formData[f] !== '' ? formData[f] : selectedRule ? null : undefined]),
      );
      const payload = {
        insurer: formData.insurer,
        rule_type: formData.rule_type,
        managed_by: formData.managed_by,
        vehicle_type: formData.vehicle_type || 'all',
        product_variant: formData.product_variant || 'all',
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        state: formData.state || null,
        city: formData.city,
        rto_code: formData.rto_code,
        pincode: formData.pincode,
        reason: formData.reason,
        ...numeric,
      } as Partial<EligibilityRule>;

      if (selectedRule) {
        await updateMutation.mutateAsync({ id: selectedRule.id, data: payload });
        toast.success('Rule updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Rule created successfully');
      }
      handleCloseRuleModal();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save rule'));
    }
  };

  const handleDelete = async () => {
    if (!selectedRule) return;
    try {
      await deleteMutation.mutateAsync(selectedRule.id);
      toast.success('Rule deleted');
      setIsDeleteModalOpen(false);
      setSelectedRule(null);
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const handleBulkImport = async () => {
    setBulkError('');
    let parsed: { rules: Partial<EligibilityRule>[] };
    try {
      parsed = JSON.parse(bulkJson);
      if (!Array.isArray(parsed.rules)) throw new Error('JSON must have a "rules" array');
    } catch (e) {
      setBulkError(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }
    try {
      const result = await bulkMutation.mutateAsync(parsed);
      toast.success(`Imported: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped`);
      setIsBulkModalOpen(false);
      setBulkJson('');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Bulk import failed'));
    }
  };

  const handleCheckEligibility = async () => {
    try {
      const { insurer_id, ...facts } = checkForm;
      const params: CheckEligibilityParams = {};
      for (const [key, value] of Object.entries(facts)) if (value) (params as Record<string, unknown>)[key] = value;
      for (const [key, value] of Object.entries(checkNumbers)) if (value !== '') (params as Record<string, unknown>)[key] = Number(value);
      await checkMutation.mutateAsync({ insurer_id, params });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Eligibility check failed'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            <span>Admin</span> / <span className="text-gray-700 dark:text-gray-200">Eligibility Rules</span>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Eligibility Rules</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage insurer eligibility rules for vehicle underwriting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            leftIcon={<BeakerIcon className="w-4 h-4" />}
            onClick={() => { setCheckForm(emptyCheck); setCheckNumbers({ cubic_capacity: '', seating_capacity: '', vehicle_age_years: '', idv: '' }); checkMutation.reset(); setIsTestModalOpen(true); }}
          >
            Test Eligibility
          </Button>
          <Button
            variant="outline"
            leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
            onClick={() => { setBulkJson(''); setBulkError(''); setIsBulkModalOpen(true); }}
          >
            Bulk Import
          </Button>
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={() => handleOpenRuleModal()}
          >
            Add Rule
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Select
            options={insurerOptions}
            value={insurerFilter}
            onChange={(e) => { setInsurerFilter(e.target.value); setPage(1); }}
            placeholder="All Insurers"
          />
          <Select
            options={ruleTypeOptions}
            value={ruleTypeFilter}
            onChange={(e) => { setRuleTypeFilter(e.target.value); setPage(1); }}
          />
          <Select
            options={vehicleTypeOptions}
            value={vehicleTypeFilter}
            onChange={(e) => { setVehicleTypeFilter(e.target.value); setPage(1); }}
          />
          <Select
            options={managedByOptions}
            value={managedByFilter}
            onChange={(e) => { setManagedByFilter(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheckIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No rules found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Add your first eligibility rule.</p>
            <Button variant="primary" className="mt-4" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => handleOpenRuleModal()}>
              Add Rule
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurer</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Vehicle Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Make / Model</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">State / City</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Max Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Managed By</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {rule.insurer_name || rule.insurer}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={rule.rule_type === 'REJECT' ? 'error' : 'success'}>
                        {rule.rule_type}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {labelOf(vehicleTypeOptions, rule.vehicle_type) || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {[rule.vehicle_make, rule.vehicle_model].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {[rule.state_name, rule.city].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {rule.max_discount_percent != null ? `${rule.max_discount_percent}%` : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {labelOf(MANAGED_BY, rule.managed_by) || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenRuleModal(rule)}>
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedRule(rule); setIsDeleteModalOpen(true); }}
                        >
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages} ({data?.count || 0} results)
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create / Edit Rule Modal */}
      <Modal
        isOpen={isRuleModalOpen}
        onClose={handleCloseRuleModal}
        title={selectedRule ? 'Edit Rule' : 'Add Eligibility Rule'}
        size="xl"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            name="insurer"
            label="Insurer"
            options={(insurersLookup || []).map((i) => ({ value: i.id, label: i.name }))}
            value={formData.insurer}
            onChange={(e) => setFormData({ ...formData, insurer: e.target.value })}
            placeholder="Select insurer"
          />
          <Select
            name="rule_type"
            label="Rule Type"
            options={[
              { value: 'REJECT', label: 'REJECT' },
              { value: 'ALLOW', label: 'ALLOW' },
            ]}
            value={formData.rule_type}
            onChange={(e) => setFormData({ ...formData, rule_type: e.target.value as 'REJECT' | 'ALLOW' })}
          />
          <Select
            name="managed_by"
            label="Managed By"
            options={MANAGED_BY}
            value={formData.managed_by}
            onChange={(e) => setFormData({ ...formData, managed_by: e.target.value })}
          />
          <Select
            name="vehicle_type"
            label="Vehicle Type"
            options={[{ value: 'all', label: 'All' }, ...VEHICLE_TYPES]}
            value={formData.vehicle_type}
            onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
          />
          <Select
            name="product_variant"
            label="Product Variant"
            options={[{ value: 'all', label: 'All' }, ...PRODUCT_VARIANTS]}
            value={formData.product_variant}
            onChange={(e) => setFormData({ ...formData, product_variant: e.target.value })}
          />
          <Input
            name="vehicle_make"
            label="Make"
            value={formData.vehicle_make}
            onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
            placeholder="e.g. MARUTI"
          />
          <Input
            name="vehicle_model"
            label="Model"
            value={formData.vehicle_model}
            onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
            placeholder="e.g. SWIFT"
          />
          <Select
            name="state"
            label="State"
            options={stateOptions}
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          />
          <Input
            name="city"
            label="City"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          />
          <Input
            name="rto_code"
            label="RTO Code"
            value={formData.rto_code}
            onChange={(e) => setFormData({ ...formData, rto_code: e.target.value })}
            placeholder="e.g. MH01"
          />
          <Input
            name="pincode"
            label="Pincode"
            value={formData.pincode}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
          />
          <Input
            name="min_cubic_capacity"
            label="Min Cubic Capacity (cc)"
            type="number"
            value={formData.min_cubic_capacity}
            onChange={(e) => setFormData({ ...formData, min_cubic_capacity: e.target.value })}
          />
          <Input
            name="max_cubic_capacity"
            label="Max Cubic Capacity (cc)"
            type="number"
            value={formData.max_cubic_capacity}
            onChange={(e) => setFormData({ ...formData, max_cubic_capacity: e.target.value })}
          />
          <Input
            name="min_seating_capacity"
            label="Min Seating Capacity"
            type="number"
            value={formData.min_seating_capacity}
            onChange={(e) => setFormData({ ...formData, min_seating_capacity: e.target.value })}
          />
          <Input
            name="max_seating_capacity"
            label="Max Seating Capacity"
            type="number"
            value={formData.max_seating_capacity}
            onChange={(e) => setFormData({ ...formData, max_seating_capacity: e.target.value })}
          />
          <Input
            name="min_vehicle_age_years"
            label="Min Vehicle Age (years)"
            type="number"
            value={formData.min_vehicle_age_years}
            onChange={(e) => setFormData({ ...formData, min_vehicle_age_years: e.target.value })}
          />
          <Input
            name="max_vehicle_age_years"
            label="Max Vehicle Age (years)"
            type="number"
            value={formData.max_vehicle_age_years}
            onChange={(e) => setFormData({ ...formData, max_vehicle_age_years: e.target.value })}
          />
          <Input
            name="min_idv"
            label="Min IDV"
            type="number"
            value={formData.min_idv}
            onChange={(e) => setFormData({ ...formData, min_idv: e.target.value })}
          />
          <Input
            name="max_idv"
            label="Max IDV"
            type="number"
            value={formData.max_idv}
            onChange={(e) => setFormData({ ...formData, max_idv: e.target.value })}
          />
          <Input
            name="max_discount_percent"
            label="Max Discount (%)"
            type="number"
            value={formData.max_discount_percent}
            onChange={(e) => setFormData({ ...formData, max_discount_percent: e.target.value })}
          />
          <Input
            name="reason"
            label="Reason (shown on rejection)"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={handleCloseRuleModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
          >
            {selectedRule ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Rule"
        size="sm"
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete this eligibility rule? This action cannot be undone.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        title="Bulk Import Rules"
        size="lg"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Paste a JSON object with a <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">rules</code> array. Each rule follows the same fields as the add rule form.
        </p>
        <textarea
          className="w-full h-56 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white font-mono p-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={'{\n  "rules": [\n    { "insurer": "uuid", "rule_type": "REJECT", "managed_by": "country_admin", "vehicle_type": "TWO_WHEELER" }\n  ]\n}'}
          value={bulkJson}
          onChange={(e) => setBulkJson(e.target.value)}
        />
        {bulkError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{bulkError}</p>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsBulkModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkImport}
            isLoading={bulkMutation.isPending}
            disabled={!bulkJson.trim()}
          >
            Import
          </Button>
        </ModalFooter>
      </Modal>

      {/* Test Eligibility Modal */}
      <Modal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        title="Test Eligibility"
        size="lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            name="check_insurer_id"
            label="Insurer"
            options={(insurersLookup || []).map((i) => ({ value: i.id, label: i.name }))}
            value={checkForm.insurer_id}
            onChange={(e) => setCheckForm({ ...checkForm, insurer_id: e.target.value })}
            placeholder="Select insurer"
          />
          <Select
            name="check_vehicle_type"
            label="Vehicle Type"
            options={[{ value: '', label: 'Select...' }, ...VEHICLE_TYPES]}
            value={checkForm.vehicle_type || ''}
            onChange={(e) => setCheckForm({ ...checkForm, vehicle_type: e.target.value })}
          />
          <Select
            name="check_product_variant"
            label="Product Variant"
            options={[{ value: '', label: 'Select...' }, ...PRODUCT_VARIANTS]}
            value={checkForm.product_variant || ''}
            onChange={(e) => setCheckForm({ ...checkForm, product_variant: e.target.value })}
          />
          <Input
            name="check_vehicle_make"
            label="Make"
            value={checkForm.vehicle_make || ''}
            onChange={(e) => setCheckForm({ ...checkForm, vehicle_make: e.target.value })}
          />
          <Input
            name="check_vehicle_model"
            label="Model"
            value={checkForm.vehicle_model || ''}
            onChange={(e) => setCheckForm({ ...checkForm, vehicle_model: e.target.value })}
          />
          <Select
            name="check_state_id"
            label="State"
            options={stateOptions}
            value={checkForm.state_id || ''}
            onChange={(e) => setCheckForm({ ...checkForm, state_id: e.target.value })}
          />
          <Input
            name="check_city"
            label="City"
            value={checkForm.city || ''}
            onChange={(e) => setCheckForm({ ...checkForm, city: e.target.value })}
          />
          <Input
            name="check_rto_code"
            label="RTO Code"
            value={checkForm.rto_code || ''}
            onChange={(e) => setCheckForm({ ...checkForm, rto_code: e.target.value })}
          />
          <Input
            name="check_pincode"
            label="Pincode"
            value={checkForm.pincode || ''}
            onChange={(e) => setCheckForm({ ...checkForm, pincode: e.target.value })}
          />
          <Input
            name="check_cubic_capacity"
            label="Cubic Capacity (cc)"
            type="number"
            value={checkNumbers.cubic_capacity}
            onChange={(e) => setCheckNumbers({ ...checkNumbers, cubic_capacity: e.target.value })}
          />
          <Input
            name="check_seating_capacity"
            label="Seating Capacity"
            type="number"
            value={checkNumbers.seating_capacity}
            onChange={(e) => setCheckNumbers({ ...checkNumbers, seating_capacity: e.target.value })}
          />
          <Input
            name="check_vehicle_age_years"
            label="Vehicle Age (years)"
            type="number"
            value={checkNumbers.vehicle_age_years}
            onChange={(e) => setCheckNumbers({ ...checkNumbers, vehicle_age_years: e.target.value })}
          />
          <Input
            name="check_idv"
            label="IDV"
            type="number"
            value={checkNumbers.idv}
            onChange={(e) => setCheckNumbers({ ...checkNumbers, idv: e.target.value })}
          />
        </div>

        {/* Result */}
        {checkMutation.data && (
          <div className={`mt-4 p-4 rounded-lg border ${checkMutation.data.eligible ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2">
              <Badge variant={checkMutation.data.eligible ? 'success' : 'error'}>
                {checkMutation.data.eligible ? 'Eligible' : 'Rejected'}
              </Badge>
              {checkMutation.data.max_discount_percent != null && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Max Discount: {checkMutation.data.max_discount_percent}%
                </span>
              )}
            </div>
            {checkMutation.data.reason && (
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{checkMutation.data.reason}</p>
            )}
          </div>
        )}

        <ModalFooter>
          <Button variant="outline" onClick={() => setIsTestModalOpen(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleCheckEligibility}
            isLoading={checkMutation.isPending}
            disabled={!checkForm.insurer_id}
          >
            Check
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default EligibilityRulesPage;
